import { Injectable } from '@angular/core';
import { AtsDate, AtsDateUnit } from '@ats/utility';
import { SettingsService } from '@cm/core/settings';
import { StorageService } from '@cm/core/storage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subject, filter, map, switchMap, take, timer } from 'rxjs';

const WARN_INTERVAL_MINUTES = 3;
const DEFAULT_EXPIRATION_MINUTES = 90;
export const EXPIRATION_TIME = 'CM-timeout';

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class CmTimeoutService {
	private readonly resetCount = new Subject<void>();
	reset$ = this.resetCount.asObservable();

	private cookieName$: Observable<string>;
	cookieName: string;

	expirationTime$: Observable<number>;
	atWarnTime$: Observable<0>;
	warnDuration$: Observable<0>;
	private expirationFromToken: number;

	constructor(private readonly storage: StorageService, private readonly settings: SettingsService) {
		this.cookieName$ = this.settings.client$.pipe(
			filter(client => !!client),
			map(client => `${EXPIRATION_TIME}.${client}`),
			take(1)
		);

		this.expirationTime$ = this.cookieName$.pipe(
			switchMap(cookieName => this.storage.getStorageItemEvents$<number>(cookieName)),
			filter(t => t > 0)
		);

		this.atWarnTime$ = this.expirationTime$.pipe(
			map(time => time - warnDuration),
			switchMap(time => timer(time))
		);

		const warnDuration = AtsDate.toMS(WARN_INTERVAL_MINUTES, AtsDateUnit.Minute);
		this.warnDuration$ = timer(warnDuration);
		this.reset$.pipe(untilDestroyed(this)).subscribe(() => {
			this.resetExpiration();
		});
	}

	resetCountdown(): void {
		this.resetCount.next();
	}

	setTimeoutToStorage(token: any): void {
		this.expirationFromToken = this.getExpiration(token);
		this.cookieName$.subscribe(cookieName => {
			this.cookieName = cookieName;
			this.storage.save(cookieName, this.expirationFromToken, DEFAULT_EXPIRATION_MINUTES);
		});
	}

	resetExpiration(): void {
		this.storage.save(this.cookieName, this.expirationFromToken, DEFAULT_EXPIRATION_MINUTES);
	}

	deleteExpiration(): void {
		this.storage.delete(this.cookieName);
	}

	private getExpiration(token: any): number {
		const expirationMinutes =
			token.absoluteExpirationMinutesRemaining < token.slidingExpirationMinutes
				? token.absoluteExpirationMinutesRemaining
				: token.slidingExpirationMinutes;
		return (
			AtsDate.toMS(expirationMinutes, AtsDateUnit.Minute) ||
			AtsDate.toMS(DEFAULT_EXPIRATION_MINUTES, AtsDateUnit.Minute)
		);
	}
}
